import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Box,
  useToast,
  Spinner,
  useDisclosure,
  Flex,
} from '@chakra-ui/react';
import { RiFolder3Line, RiAddLine, RiCheckLine } from 'react-icons/ri';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';
import { CreateCollectionModal } from './CreateCollectionModal';
import {
  useGetMyAgentCollectionsQuery,
  useAddAgentToCollectionMutation,
  useAddDigitalTwinToCollectionMutation,
  useRemoveAgentFromCollectionMutation,
  useRemoveDigitalTwinFromCollectionMutation,
} from '../../generated/graphql';

interface AddToCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  agentName: string;
  agentType?: 'custom' | 'digitalTwin';
}

export const AddToCollectionModal = ({
  isOpen,
  onClose,
  agentId,
  agentName,
  agentType = 'custom',
}: AddToCollectionModalProps) => {
  const toast = useToast();
  const createCollectionModal = useDisclosure();

  const { data, loading, refetch } = useGetMyAgentCollectionsQuery({
    skip: !isOpen,
  });

  const [addAgentToCollection, { loading: addingAgent }] = useAddAgentToCollectionMutation();
  const [addDigitalTwinToCollection, { loading: addingTwin }] = useAddDigitalTwinToCollectionMutation();
  const [removeAgentFromCollection, { loading: removingAgent }] = useRemoveAgentFromCollectionMutation();
  const [removeDigitalTwinFromCollection, { loading: removingTwin }] = useRemoveDigitalTwinFromCollectionMutation();

  const processing = addingAgent || addingTwin || removingAgent || removingTwin;
  const collections = data?.getMyAgentCollections || [];

  // Check if the current agent/twin is in a given collection
  const isInCollection = (collection: typeof collections[0]): boolean => {
    if (agentType === 'digitalTwin') {
      return collection.digitalTwins?.some(dt => dt.id === agentId) ?? false;
    }
    return collection.agents?.some(a => a.id === agentId) ?? false;
  };

  const handleToggleCollection = async (collection: typeof collections[0]) => {
    const alreadyInCollection = isInCollection(collection);

    try {
      if (alreadyInCollection) {
        // Remove from collection
        if (agentType === 'digitalTwin') {
          await removeDigitalTwinFromCollection({
            variables: {
              collectionId: collection.id,
              digitalTwinId: agentId,
            },
          });
        } else {
          await removeAgentFromCollection({
            variables: {
              collectionId: collection.id,
              agentId,
            },
          });
        }

        toast({
          title: 'Removed from collection',
          description: `"${agentName}" has been removed from "${collection.name}".`,
          status: 'success',
          duration: 3000,
        });
      } else {
        // Add to collection
        if (agentType === 'digitalTwin') {
          await addDigitalTwinToCollection({
            variables: {
              collectionId: collection.id,
              digitalTwinId: agentId,
            },
          });
        } else {
          await addAgentToCollection({
            variables: {
              collectionId: collection.id,
              agentId,
            },
          });
        }

        toast({
          title: 'Added to collection',
          description: `"${agentName}" has been added to "${collection.name}".`,
          status: 'success',
          duration: 3000,
        });
      }

      await refetch();
    } catch (error: any) {
      toast({
        title: alreadyInCollection ? 'Error removing from collection' : 'Error adding to collection',
        description: error.message || 'Something went wrong.',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleCollectionCreated = () => {
    refetch();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent bg={HelivanColor.background.primary}>
          <ModalHeader>
            <HelivanText variant="sectionHeader">Add to Collection</HelivanText>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Flex align="center" mb={2}>
                <RiFolder3Line size={20} color={HelivanColor.text.blueMid} />
                <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid} style={{ marginLeft: 8 }}>
                  Add "{agentName}" to one of your collections
                </HelivanText>
              </Flex>

              {loading ? (
                <Box textAlign="center" py={4}>
                  <Spinner color={HelivanColor.text.helivanBlue} />
                </Box>
              ) : collections.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                    You don't have any collections yet.
                  </HelivanText>
                </Box>
              ) : (
                <VStack spacing={2} align="stretch">
                  {collections.map((collection) => {
                    const inCollection = isInCollection(collection);
                    return (
                      <HStack
                        key={collection.id}
                        p={3}
                        borderRadius="md"
                        border="1px solid"
                        borderColor={inCollection ? HelivanColor.text.helivanBlue : HelivanColor.border.primary}
                        bg={inCollection ? 'rgba(0, 71, 125, 0.05)' : 'white'}
                        _hover={{ bg: inCollection ? 'rgba(0, 71, 125, 0.1)' : HelivanColor.background.secondary }}
                        cursor="pointer"
                        onClick={() => handleToggleCollection(collection)}
                        justifyContent="space-between"
                      >
                        <HStack spacing={3}>
                          <RiFolder3Line size={18} color={inCollection ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid} />
                          <VStack align="start" spacing={0}>
                            <HelivanText variant="smallBody">{collection.name}</HelivanText>
                            <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                              {collection.agentCount + (collection.digitalTwinCount || 0)} item{(collection.agentCount + (collection.digitalTwinCount || 0)) !== 1 ? 's' : ''}
                            </HelivanText>
                          </VStack>
                        </HStack>
                        {processing ? (
                          <Spinner size="sm" color={HelivanColor.text.helivanBlue} />
                        ) : inCollection ? (
                          <RiCheckLine size={16} color={HelivanColor.text.helivanBlue} />
                        ) : (
                          <RiAddLine size={16} color={HelivanColor.text.blueMid} />
                        )}
                      </HStack>
                    );
                  })}
                </VStack>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Flex width="100%" justify="space-between">
              <HelivanButton
                variant="outline"
                onClick={createCollectionModal.onOpen}
              >
                <HStack spacing={2}>
                  <RiAddLine size={14} />
                  <span>New Collection</span>
                </HStack>
              </HelivanButton>
              <HelivanButton variant="ghost" onClick={onClose}>
                Close
              </HelivanButton>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CreateCollectionModal
        isOpen={createCollectionModal.isOpen}
        onClose={createCollectionModal.onClose}
        onSuccess={handleCollectionCreated}
      />
    </>
  );
};
